import React, { useState } from 'react';
import {
    Box,
    Typography,
    Grid,
    Paper,
    Divider,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Chip,
    Card,
    CardContent,
    Link,
    Button,
    Avatar
} from '@mui/material';
import {
    CheckCircleOutline,
    Group,
    ShoppingCart,
    TipsAndUpdates,
    EmojiObjects,
    LocalFireDepartment,
    FlashlightOn,
    Radio,
    Restaurant,
    WaterDrop,
    MedicalServices,
    Home,
    Close,
    FormatListNumbered
} from '@mui/icons-material';

// スクロール用の関数
const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
        const offset = 80; // ヘッダーの高さ分のオフセット
        const elementPosition = element.getBoundingClientRect().top;
        const offsetPosition = elementPosition + window.pageYOffset - offset;

        window.scrollTo({
            top: offsetPosition,
            behavior: 'smooth'
        });
    }
};

const tocItems = [
    { id: 'introduction', label: 'ルームシェアで防災グッズが必要な理由' },
    { id: 'list', label: 'おすすめ防災グッズリスト' },
    { id: 'tips', label: '共同購入のコツとポイント' },
    { id: 'usage', label: 'グッズの使い方とシェアの工夫' },
    { id: 'summary', label: 'まとめ・今すぐできる防災対策' }
];

const PostContent = () => {
    const [showToc, setShowToc] = useState(true);
    return (
        <Box sx={{ maxWidth: 800, mx: 'auto', px: 3 }}>
            {/* 目次 */}
            {showToc ? (
                <Card sx={{ mb: 4, boxShadow: 2, bgcolor: '#e3f2fd', border: '1px solid #ddd', borderRadius: 2 }}>
                    <CardContent sx={{ p: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                            <Typography variant="h6" sx={{ flexGrow: 1 }}>目次</Typography>
                            <Button
                                variant="outlined"
                                size="small"
                                onClick={() => setShowToc(false)}
                                sx={{ ml: 2, minWidth: 40 }}
                                startIcon={<Close fontSize="small" />}
                            >
                                閉じる
                            </Button>
                        </Box>
                        <List sx={{ p: 0 }}>
                            {tocItems.map((item, idx) => (
                                <ListItem key={item.id} sx={{ p: 0.5, display: 'flex', alignItems: 'center' }}>
                                    <Box sx={{
                                        minWidth: 32, minHeight: 32,
                                        bgcolor: '#f5f5f5',
                                        color: '#888',
                                        fontWeight: 'bold',
                                        borderRadius: 1,
                                        fontSize: '1.15rem',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        mr: 1
                                    }}>
                                        {idx + 1}.
                                    </Box>
                                    <Link
                                        component="button"
                                        onClick={() => scrollToSection(item.id)}
                                        color="inherit"
                                        underline="hover"
                                        sx={{ cursor: 'pointer', fontSize: '0.97rem', textAlign: 'left' }}
                                    >
                                        {item.label}
                                    </Link>
                                </ListItem>
                            ))}
                        </List>
                    </CardContent>
                </Card>
            ) : (
                <Button
                    variant="outlined"
                    size="small"
                    onClick={() => setShowToc(true)}
                    sx={{ mb: 2 }}
                    startIcon={<FormatListNumbered />}
                >
                    目次を表示
                </Button>
            )}

            {/* 導入 */}
            <Box id="introduction" sx={{ mb: 4 }}>
                <Typography variant="h4" component="h2" gutterBottom>
                    ルームシェアで防災グッズが必要な理由
                </Typography>
                <Typography variant="body1" paragraph>
                    住まいの安全性への意識が高まる中、<b>ルームシェア生活での防災対策</b>は今や欠かせないテーマになっています。複数人で暮らすからこそ、
                    災害時に頼れるのは自分たちの備え。地震・火災・台風・停電…<b>「もしも」のときに役立つ防災グッズ</b>を揃えておくことが、日々の安心に直結します。
                </Typography>
                <Typography variant="body1" paragraph>
                    この記事では、<b>ルームシェアならではの視点</b>から「本当に役立つ防災グッズ」を厳選し、
                    <Chip label="防災グッズリスト" size="small" color="primary" variant="outlined" sx={{ mx: 0.5 }} />
                    <Chip label="共同購入のコツ" size="small" color="primary" variant="outlined" sx={{ mx: 0.5 }} />
                    <Chip label="使い方" size="small" color="primary" variant="outlined" sx={{ mx: 0.5 }} />
                    <Chip label="体験談" size="small" color="primary" variant="outlined" sx={{ mx: 0.5 }} />
                    まで、実践的にご紹介します。
                </Typography>
                <Box sx={{ my: 3, borderRadius: 2, overflow: 'hidden' }}>

                </Box>
                <Typography variant="body2" color="textSecondary">
                    みんなで防災意識を高め、快適なルームシェア生活を守りましょう。
                </Typography>
            </Box>

            {/* おすすめ防災グッズリスト */}
            <Box id="list" sx={{ mb: 4 }}>
                <Divider sx={{ my: 2 }} />
                <Typography variant="h4" component="h2" gutterBottom>
                    おすすめ防災グッズリスト
                </Typography>
                <Typography variant="body1" paragraph>
                    ルームシェアでは<b>「個人用」「共用グッズ」</b>のバランスがポイント。必要最低限を分担し、効率よく備えましょう。
                </Typography>
                <Grid container spacing={2} sx={{ mb: 2 }}>
                    <Grid item xs={12} sm={6}>
                        <Card sx={{ maxWidth: 400, mb: 2 }}>
                            <CardContent>
                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                    <FlashlightOn sx={{ color: '#1976d2', mr: 1 }} />
                                    <Typography variant="h6" sx={{ fontWeight: 'bold' }}>懐中電灯・ランタン</Typography>
                                </Box>
                                <Typography variant="body2">
                                    停電時の必需品。<b>人数分＋共用</b>で準備を。LEDタイプやUSB充電式は長持ち＆便利。
                                </Typography>
                            </CardContent>
                        </Card>
                        <Card sx={{ maxWidth: 400, mb: 2 }}>
                            <CardContent>
                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                    <Radio sx={{ color: '#0288d1', mr: 1 }} />
                                    <Typography variant="h6" sx={{ fontWeight: 'bold' }}>携帯ラジオ</Typography>
                                </Box>
                                <Typography variant="body2">
                                    最新情報を入手するために。<b>電池式＋手回し充電</b>がベスト。
                                </Typography>
                            </CardContent>
                        </Card>
                        <Card sx={{ maxWidth: 400, mb: 2 }}>
                            <CardContent>
                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                    <WaterDrop sx={{ color: '#039be5', mr: 1 }} />
                                    <Typography variant="h6" sx={{ fontWeight: 'bold' }}>飲料水・ウォータータンク</Typography>
                                </Box>
                                <Typography variant="body2">
                                    <b>1人1日3Lを目安に、最低3日分</b>。共用の大容量タンクが便利。
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <Card sx={{ maxWidth: 400, mb: 2 }}>
                            <CardContent>
                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                    <Restaurant sx={{ color: '#c62828', mr: 1 }} />
                                    <Typography variant="h6" sx={{ fontWeight: 'bold' }}>非常食</Typography>
                                </Box>
                                <Typography variant="body2">
                                    レトルト・缶詰・フリーズドライなど<b>好みやアレルギーに合わせて</b>。共用＋各自で備蓄を。
                                </Typography>
                            </CardContent>
                        </Card>
                        <Card sx={{ maxWidth: 400, mb: 2 }}>
                            <CardContent>
                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                    <MedicalServices sx={{ color: '#2e7d32', mr: 1 }} />
                                    <Typography variant="h6" sx={{ fontWeight: 'bold' }}>救急セット</Typography>
                                </Box>
                                <Typography variant="body2">
                                    絆創膏・消毒液・常備薬など。<b>共用1セット</b>をわかりやすい場所に。
                                </Typography>
                            </CardContent>
                        </Card>
                        <Card sx={{ maxWidth: 400, mb: 2 }}>
                            <CardContent>
                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                    <LocalFireDepartment sx={{ color: '#ef6c00', mr: 1 }} />
                                    <Typography variant="h6" sx={{ fontWeight: 'bold' }}>簡易消火器・火災警報器</Typography>
                                </Box>
                                <Typography variant="body2">
                                    キッチンや出入口付近に設置。<b>全員で使い方を確認</b>しておこう。
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>
                <Divider sx={{ my: 2 }} />
                <Paper elevation={0} sx={{ bgcolor: '#f9fbe7', p: 2, borderRadius: 2, mb: 2 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
                        他にもあると便利なもの
                    </Typography>
                    <List dense>
                        <ListItem>
                            <ListItemIcon><EmojiObjects sx={{ color: '#fbc02d' }} /></ListItemIcon>
                            <ListItemText primary="モバイルバッテリー（スマホ充電用）" />
                        </ListItem>
                        <ListItem>
                            <ListItemIcon><Home sx={{ color: '#90caf9' }} /></ListItemIcon>
                            <ListItemText primary="簡易トイレ・ポリ袋" />
                        </ListItem>
                        <ListItem>
                            <ListItemIcon><CheckCircleOutline sx={{ color: '#4caf50' }} /></ListItemIcon>
                            <ListItemText primary="防寒グッズ（毛布、カイロなど）" />
                        </ListItem>
                        <ListItem>
                            <ListItemIcon><TipsAndUpdates sx={{ color: '#ffb300' }} /></ListItemIcon>
                            <ListItemText primary="ヘルメット・軍手" />
                        </ListItem>
                    </List>
                </Paper>
                <Box sx={{ my: 3, borderRadius: 2, overflow: 'hidden' }}>

                </Box>
            </Box>

            {/* 共同購入のコツとポイント */}
            <Box id="tips" sx={{ mb: 4 }}>
                <Divider sx={{ my: 2 }} />
                <Typography variant="h4" component="h2" gutterBottom>
                    共同購入のコツとポイント
                </Typography>
                <Typography variant="body1" paragraph>
                    防災グッズは「<b>共用で揃えるもの」と「個人で持つもの</b>」を明確にし、効率よく準備しましょう。みんなで話し合うことでコストも抑えられます。
                </Typography>
                <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                        <Paper elevation={1} sx={{ p: 2, borderRadius: 2, mb: 1 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                <Group sx={{ color: '#1976d2', mr: 1 }} />
                                <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>共用で買うもの</Typography>
                            </Box>
                            <List dense>
                                <ListItem>
                                    <ListItemIcon><Radio sx={{ color: '#0288d1' }} /></ListItemIcon>
                                    <ListItemText primary="携帯ラジオ" />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon><MedicalServices sx={{ color: '#2e7d32' }} /></ListItemIcon>
                                    <ListItemText primary="救急セット" />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon><LocalFireDepartment sx={{ color: '#ef6c00' }} /></ListItemIcon>
                                    <ListItemText primary="消火器・火災警報器" />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon><WaterDrop sx={{ color: '#039be5' }} /></ListItemIcon>
                                    <ListItemText primary="大容量飲料水・タンク" />
                                </ListItem>
                            </List>
                        </Paper>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <Paper elevation={1} sx={{ p: 2, borderRadius: 2, mb: 1 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                <ShoppingCart sx={{ color: '#388e3c', mr: 1 }} />
                                <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>個人で揃えるもの</Typography>
                            </Box>
                            <List dense>
                                <ListItem>
                                    <ListItemIcon><FlashlightOn sx={{ color: '#1976d2' }} /></ListItemIcon>
                                    <ListItemText primary="懐中電灯" />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon><Restaurant sx={{ color: '#c62828' }} /></ListItemIcon>
                                    <ListItemText primary="非常食（アレルギー・好みに合わせて）" />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon><EmojiObjects sx={{ color: '#fbc02d' }} /></ListItemIcon>
                                    <ListItemText primary="モバイルバッテリー" />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon><CheckCircleOutline sx={{ color: '#4caf50' }} /></ListItemIcon>
                                    <ListItemText primary="防寒グッズ" />
                                </ListItem>
                            </List>
                        </Paper>
                    </Grid>
                </Grid>
                <Paper elevation={0} sx={{ bgcolor: '#fffde7', p: 2, borderRadius: 2, mt: 3 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
                        <TipsAndUpdates sx={{ color: '#ffb300', mr: 1 }} />
                        共同購入の進め方
                    </Typography>
                    <List dense>
                        <ListItem>
                            <ListItemIcon><CheckCircleOutline sx={{ color: '#4caf50' }} /></ListItemIcon>
                            <ListItemText primary="人数・必要数をリストアップ（Googleスプレッドシート等が便利）" />
                        </ListItem>
                        <ListItem>
                            <ListItemIcon><CheckCircleOutline sx={{ color: '#4caf50' }} /></ListItemIcon>
                            <ListItemText primary="役割分担（購入担当・保管担当など）" />
                        </ListItem>
                        <ListItem>
                            <ListItemIcon><CheckCircleOutline sx={{ color: '#4caf50' }} /></ListItemIcon>
                            <ListItemText primary="費用は均等または利用頻度で分担" />
                        </ListItem>
                        <ListItem>
                            <ListItemIcon><CheckCircleOutline sx={{ color: '#4caf50' }} /></ListItemIcon>
                            <ListItemText primary="年1回の点検・入れ替えを全員で実施" />
                        </ListItem>
                    </List>
                </Paper>
            </Box>

            {/* グッズの使い方とシェアの工夫 */}
            <Box id="usage" sx={{ mb: 4 }}>
                <Divider sx={{ my: 2 }} />
                <Typography variant="h4" component="h2" gutterBottom>
                    グッズの使い方とシェアの工夫
                </Typography>
                <Typography variant="body1" paragraph>
                    「<b>いざという時に誰でも使える</b>」ためには、<b>見える収納</b>と<b>情報共有</b>が肝心です。
                </Typography>
                <Paper elevation={0} sx={{ bgcolor: '#e3f2fd', p: 2, borderRadius: 2, mb: 3 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
                        ルームシェア防災グッズ管理のポイント
                    </Typography>
                    <List dense>
                        <ListItem>
                            <ListItemIcon><EmojiObjects sx={{ color: '#fbc02d' }} /></ListItemIcon>
                            <ListItemText primary="玄関・リビングなど全員がすぐ取り出せる場所に保管" />
                        </ListItem>
                        <ListItem>
                            <ListItemIcon><TipsAndUpdates sx={{ color: '#ffb300' }} /></ListItemIcon>
                            <ListItemText primary="グッズリストを冷蔵庫や掲示板に貼り、担当者・保管場所も明記" />
                        </ListItem>
                        <ListItem>
                            <ListItemIcon><CheckCircleOutline sx={{ color: '#4caf50' }} /></ListItemIcon>
                            <ListItemText primary="懐中電灯・ラジオ・消火器などは実際に使ってみて全員で体験" />
                        </ListItem>
                        <ListItem>
                            <ListItemIcon><Group sx={{ color: '#1976d2' }} /></ListItemIcon>
                            <ListItemText primary="月1回のミーティングで点検・情報共有" />
                        </ListItem>
                    </List>
                </Paper>
                <Box sx={{ my: 3, borderRadius: 2, overflow: 'hidden' }}>
                    <img
                        src="/images/blog/evacuation-drill_roomshare-bousai-goods.jpg"
                        alt="共用スペースでルームメイトが防災リュックを背負い、避難訓練を実施している様子。カラフルな防災用品が並び、協力し合う雰囲気"
                        style={{ width: '100%', display: 'block', borderRadius: 8 }}
                    />
                </Box>
                <Typography variant="body1" paragraph>
                    <b>「やってみる」「共有する」</b>ことで、緊急時も落ち着いて行動できる自信につながります。
                </Typography>
            </Box>


            {/* まとめ・今すぐできる防災対策 */}
            <Box id="summary" sx={{ mb: 4 }}>
                <Divider sx={{ my: 2 }} />
                <Typography variant="h4" component="h2" gutterBottom>
                    まとめ・今すぐできる防災対策
                </Typography>
                <Typography variant="body1" paragraph>
                    ルームシェア生活における防災グッズの備えは、「自分たちの安心」を守る最初の一歩。
                    みんなで話し合いながら<b>楽しく・効率よく</b>準備することで、日々の暮らしがより豊かになります。
                </Typography>
                <Paper elevation={0} sx={{ bgcolor: '#f1f8e9', p: 2, borderRadius: 2, mb: 2 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
                        <TipsAndUpdates sx={{ color: '#ffb300', mr: 1 }} />
                        今すぐできるワンポイント
                    </Typography>
                    <List dense>
                        <ListItem>
                            <ListItemIcon><CheckCircleOutline sx={{ color: '#4caf50' }} /></ListItemIcon>
                            <ListItemText primary="まずは最低限の懐中電灯・水・非常食だけでも全員分用意" />
                        </ListItem>
                        <ListItem>
                            <ListItemIcon><CheckCircleOutline sx={{ color: '#4caf50' }} /></ListItemIcon>
                            <ListItemText primary="防災グッズの保管場所をみんなで確認・共有" />
                        </ListItem>
                        <ListItem>
                            <ListItemIcon><CheckCircleOutline sx={{ color: '#4caf50' }} /></ListItemIcon>
                            <ListItemText primary="月1回のミーティングで点検・使い方の確認を習慣に" />
                        </ListItem>
                    </List>
                </Paper>
                <Typography variant="body2" color="textSecondary" sx={{ mt: 2 }}>
                    今からでも遅くありません。みんなで話し合い、安心・快適なルームシェア生活を築いていきましょう！
                </Typography>
                <Box sx={{ my: 3, borderRadius: 2, overflow: 'hidden' }}>
                    <img
                        src="/images/blog/group-preparedness_roomshare-bousai-goods.jpg"
                        alt="みんなで防災グッズを囲みながら、明るいリビングで計画的に備蓄内容を確認し合うルームメイトたち。統一感のある収納ボックスが並び、協力的な雰囲気"
                        style={{ width: '100%', display: 'block', borderRadius: 8 }}
                    />
                </Box>
            </Box>
        </Box>
    );
};

const post = {
    id: 'roomshare-bousai-goods',
    slug: 'roomshare-bousai-goods',
    title: 'ルームシェアの「おすすめ防災グッズ」特集',
    excerpt: '防災グッズリスト 共同購入のコツ 使い方 体験談',
    coverImage: '/images/blog/cover-roomshare-bousai-goods_roomshare-bousai-goods.jpg',
    coverImageAlt: '明るい自然光が差し込むモダンなルームシェアの共有リビングで、多様な背景を持つルームメイトたちが防災グッズを囲みながら協力的に話し合いをしている様子。カラフルな非常用持ち出し袋や水、懐中電灯、非常食などがテーブルに並び、安心感と信頼感が伝わる雰囲気。',
    publishedAt: '2024-06-18T11:00:00.000Z',
    isPublished: true,
    categories: ['ルームシェア', '防災', '暮らしの知恵'],
    tags: ['防災グッズ', 'ルームシェア', '共同購入', '防災体験談'],
    author: {
        name: '山本 さくら',
        avatar: '/images/avatars/sakura-yamamoto.jpg',
        bio: 'ルームシェア歴10年。防災士資格を持ち、共同生活に役立つ知識や実体験をもとに安心で快適な暮らしを提案しています。',
        profilePicture: '/images/avatars/sakura-yamamoto.jpg'
    },
    viewCount: 1320,
    contentComponent: PostContent
};

export default post;